import { ActionButton, formatTime, PedagogyUserData, ReactIcons, RemoteController, RemoteControllerSections, useTimer } from "@kalyzee/escaet-ui";
import { useMemo, useState } from "react";
import { useMultiMeetingSelector } from "../../hooks/meeting";
import { usePedagogyUsers } from "../../hooks/pedagogy";
import { usePedagogyAvailableViews, usePopulatedMeetingData } from "../../hooks/populatedData";
import { PopulatedMeetingSession } from "../../interfaces/populatedData";
import { usePedagogyContext } from "../../pages/master/pedagogy/context";
import { useUser } from "../../store/session/hooks";
import { ExpertsToRemotesContainer } from "../player/container/ExpertsToRemotesContainer";

import DashboardChat from "../dashboard/Chat";
import styles from "./WaitingRoom.module.css";
import { MeetingPedagogyUserData } from "../../interfaces/pedagogy";
import { hasBadBehaviors } from "../../helpers/pedagogy";

const Timer = ({ date }: { date: Date }) => {
  const timer = useTimer({ startDate: date });
  const humanTime = formatTime(timer.seconds, true);
  return humanTime;
};

const PlayerOverylay = ({ user, children }: { user: PedagogyUserData; children: React.ReactNode }) => {
  if (!user?.message && !user?.lateSince) return <>{children}</>;
  return (
    <div className={styles.player}>
      {children}
      <div className={styles.playerOverlay}>
        <div className={styles.playerInfoBtn}>
          i
        </div>
        <div className={styles.playerOverlayBody}>
          {user.lateSince && (
            <div className={styles.playerOverlayBodyTitle}>
              <Timer date={new Date(user.lateSince)} />
            </div>
          )}
          {user.message}
        </div>
      </div>
    </div>
  );
};

export interface WaitingRoomContainerProps {
  onMessagesUnread?: (count: number) => void;
}

export const WaitingRoomContainer = ({ onMessagesUnread }: WaitingRoomContainerProps) => {
  const selfUser = useUser();
  const pedagoCtx = usePedagogyContext();

  // Pedago Meeting Data
  const pedagoContainer = pedagoCtx?.pedagogyContainers?.length ? pedagoCtx.pedagogyContainers[0] : undefined;
  const pedagoAPI = pedagoContainer?.getAPI();
  const populatedData = usePopulatedMeetingData(pedagoAPI);
  const views = usePedagogyAvailableViews(populatedData, selfUser?.id);
  const currentView = views[0];

  // Here we collect pedagogy users from other meetings
  // (their origin meetings known and joined thanks to the useEffect below)
  const nonPedaContainers = useMemo(() => pedagoCtx?.containersExceptPedagogy?.map((c) => c.getAPI()) ?? [], [pedagoCtx]);
  const [nonPedaMeetings] = useMultiMeetingSelector(nonPedaContainers, (state) => state.meeting);
  const pedagogyUsers = usePedagogyUsers();

  // we bind them together
  const pedagoSessionsWithOriginUsers = useMemo(() => {
    const users: {
      pedagoSession: PopulatedMeetingSession;
      originUser: MeetingPedagogyUserData;
    }[] = [];
    currentView.students.forEach((student) => {
      if (!student.video) return;

      const pedagoUser = pedagogyUsers.find((u) => u.id === student.session.user.id);
      if (!pedagoUser) return;

      users.push({
        pedagoSession: student.session,
        originUser: pedagoUser,
      });
    });
    return users;
  }, [currentView, pedagogyUsers]);

  const uniqueTargets = useMemo(() => Array.from(new Set(pedagoSessionsWithOriginUsers.map(({ originUser }) => originUser))), [pedagoSessionsWithOriginUsers]);

  // Normal users = not late and no alerts
  const atLeastOneUserInWaitingRoom = uniqueTargets.some((t) => t.inWaitingRoom);
  const atLeastOneUserWithAlert = uniqueTargets.some((t) => hasBadBehaviors(t));
  // const atLeastOneUserNormalUser = uniqueTargets.some((t) => !t.inWaitingRoom && !hasBadBehaviors(t));

  const actions = useMemo(() => {
    const actions = pedagoCtx?.actions?.filter((a) => (!atLeastOneUserInWaitingRoom && !atLeastOneUserWithAlert ? ["exclude", "warn", "notify"] : ["exclude", "warn", "notify", "tolerate"]).includes(a.type)) ?? [];
    return actions.map((a) => {
      let newName: string | undefined = undefined;
      if (a.type === "tolerate") newName = "Tolérer";
      // if (a.type === "warn") newName = atLeastOneUserNormalUser ? "Avertir" : a.name;
      return newName ? {...a, name: newName } : a;
    }) ?? []
  }, [pedagoCtx, atLeastOneUserInWaitingRoom, atLeastOneUserWithAlert]);

  const [unreadMessages, setUnreadMessages] = useState(0);
  const [remoteControllerState, setRemoteControllerState] = useState<{ chat: "closed" | "opened" }>({
    chat: "opened",
  });

  const renderHeader = () => {
    const userNb = uniqueTargets.length;

    let notchText: string;

    if (!atLeastOneUserInWaitingRoom) {
      if (userNb === 1) {
        const meeting = nonPedaMeetings.find((m) => m?.room?.id === uniqueTargets[0].roomId);
        const roomName = meeting?.room?.name;
        notchText = `Communication avec ${uniqueTargets[0].firstName} ${roomName ? `(${roomName})` : ""}`;
      } else {
        notchText = `Communication avec ${userNb} participant${userNb > 1 ? "s" : ""}`;
      }
    } else {
      notchText = `Communication avec ${userNb} participant${userNb > 1 ? "s" : ""} en salle d'attente`;
    }

    const NotchIcon = ReactIcons.GroupMin;

    return (
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          backgroundColor: "#444444",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "1em",
            alignItems: "center",
            padding: "10px 20px",
          }}
        >
          <NotchIcon
            style={{
              width: 24,
              height: 24,
              color: "#fff9",
            }}
          />
          <div
            style={{
              fontSize: 20,
              fontWeight: 900,
              color: "white",
            }}
          >
            {notchText.toUpperCase()}
          </div>
        </div>
      </div>
    );
  };

  const renderRemoteController = () => {
    const group = currentView.group?.room;
    const chatroom = group?.chatrooms?.[0];
    return (
      <RemoteController
        style={{ display: pedagoCtx?.displayRemoteController ? "inherit" : "none", height: "100%", width: 400, zoom: 1 }}
        stylePiP={{ zoom: 1.1 }}
        footerElements={actions?.map((action, index) => {
          let targets = uniqueTargets;
          if (action.type === "tolerate") {
            targets = uniqueTargets.filter((t) => t.inWaitingRoom || hasBadBehaviors(t));
          }
          return <ActionButton style={{ width: '100%'}} key={`grouped-action-${index}`} type="large_colored" action={action} targets={targets} />
        })}
      >
        {chatroom ? (
          <RemoteControllerSections.Chat
            defaultOpened={remoteControllerState.chat === "opened"}
            disableUnmountContent
            badge={unreadMessages ? `${unreadMessages}` : undefined}
            onOpen={(v: boolean) => setRemoteControllerState((s) => ({ ...s, chat: v ? "opened" : "closed" }))}
          >
            <DashboardChat
              chatroom={chatroom}
              inactive={remoteControllerState.chat === "closed"}
              hideHeader
              meetingContainer={pedagoAPI}
              style={{ width: "100%", height: "40vh" }}
              onUnreadMessages={(messages) => {
                setUnreadMessages(messages.length);
                onMessagesUnread?.(messages.length);
              }}
            />
          </RemoteControllerSections.Chat>
        ) : null}
      </RemoteController>
    );
  };

  if (currentView.type !== "communication") {
    return null;
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {renderHeader()}
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          minHeight: 0,
          padding: "0.5em",
        }}
      >
        <ExpertsToRemotesContainer
          meetingContainerAPI={pedagoAPI}
          context={pedagoCtx}
          expertSessions={currentView?.experts?.map((data) => data.session)}
          remoteSessions={pedagoSessionsWithOriginUsers.map(({ pedagoSession }) => pedagoSession)}
          showSelf={"new_source"} // TODO-v4 : current_video ?
          renderItemExtraButtons={(item) => {
            const session = item.getData().extra?.meetingSession;
            if (!session) return null;
            const sessionWithUser = pedagoSessionsWithOriginUsers.find((s) => s.pedagoSession.id === session.id);
            if (!sessionWithUser) return null;
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  borderRadius: "0.25em",
                  overflow: "hidden",
                }}
              >
                {actions?.map((action, index) => {
                  if (action.type === "tolerate" && !sessionWithUser.originUser.inWaitingRoom && !hasBadBehaviors(sessionWithUser.originUser)) return null;
                  return (
                    <div style={{ width: 70 }}>
                    <ActionButton key={`action-${index}`} type="small" targets={[sessionWithUser.originUser]} action={action} />
                  </div>
                  );
                })}
              </div>
            );
          }}
          renderItem={(item, children) => {
            const session = item.getData().extra?.meetingSession;
            if (!session) return null;
            const sessionWithUser = pedagoSessionsWithOriginUsers.find((s) => s.pedagoSession.id === session.id);
            if (!sessionWithUser) return null;
            return <PlayerOverylay user={sessionWithUser.originUser}>{children}</PlayerOverylay>;
          }}
          style={{ padding: "1em" }}
        />
        {renderRemoteController()}
      </div>
    </div>
  );
};
